import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { breadcrumbSchema } from "@/components/JsonLd";

export default function CustomPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/custom-pages/${slug}`);
        if (!cancelled) setPage(data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <PageLayout>
        <Seo title="Page not found" noindex />
        <section className="pt-40 pb-24 min-h-[60vh] flex items-center" data-testid="custom-page-404">
          <div className="container-tti text-center">
            <div className="eyebrow text-deepred mb-4">404</div>
            <h1 className="font-serif text-5xl md:text-6xl mb-4">We couldn&rsquo;t find that page.</h1>
            <p className="text-foreground/70 mb-8">The page you&rsquo;re looking for may have been moved or removed.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-deepred text-white text-sm uppercase tracking-wider hover:bg-saffron transition-colors"
              data-testid="custom-page-back-home"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </section>
      </PageLayout>
    );
  }

  if (!page) {
    return (
      <PageLayout>
        <div className="pt-40 pb-24 min-h-[60vh] flex items-center justify-center" data-testid="custom-page-loading">
          <div className="animate-pulse text-foreground/50">Loading…</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Seo
        title={page.title}
        description={page.meta_description || page.hero_subtitle?.slice(0, 160)}
        image={page.hero_image}
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: page.title, path: `/pages/${page.slug}` },
        ])}
      />
      <PageHero
        eyebrow="Tour Trips India Holidays"
        title={page.title}
        subtitle={page.hero_subtitle || ""}
        image={page.hero_image || "https://images.pexels.com/photos/19756404/pexels-photo-19756404.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"}
      />
      <section className="py-20 md:py-24 bg-cream-100" data-testid={`custom-page-${page.slug}`}>
        <div className="container-tti max-w-4xl">
          {(page.sections || []).map((s, i) => (
            <div key={i} className="mb-14" data-testid={`custom-page-section-${i}`}>
              {s.heading && (
                <h2 className="font-serif text-3xl md:text-4xl text-deepred mb-5 leading-snug">
                  {s.heading}
                </h2>
              )}
              {s.body && (
                <div className="prose prose-lg max-w-none text-foreground/80 font-light leading-relaxed whitespace-pre-line">
                  {s.body}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
